import type * as ld from "launchdarkly-js-client-sdk";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { createContext, useContext, useEffect, useRef } from "react";
import type { Flag, FlagBootstrap } from "@medbillai/utils/flag";
import { ldContextIdentityKey, useFlagIdentifyGeneration } from "./shared";
const BootstrapContext = createContext<FlagBootstrap | undefined>(undefined);
export const FlagBootstrapProvider = ({
  bootstrap,
  children
}: {
  bootstrap?: FlagBootstrap;
  children?: React.ReactNode;
}) =>
// Read straight from the prop rather than latched into state: this provider
// is above the page in `_app` and is not remounted across client-side
// navigations, so latching would render a later page with an earlier
// page's snapshot.
<BootstrapContext.Provider value={bootstrap} data-sentry-element="BootstrapContext.Provider" data-sentry-component="FlagBootstrapProvider" data-sentry-source-file="bootstrap.tsx">
    {children}
  </BootstrapContext.Provider>;
export const useFlagBootstrap = () => useContext(BootstrapContext);

/**
 * Whether two contexts denote the same visitor. `ldContextIdentityKey` answers
 * `null` for every anonymous context, which is the right identity semantic for
 * the read latch but would let one anonymous key enrol against another here —
 * so anonymous contexts are compared by key.
 */
const sameIdentity = (a?: ld.LDContext, b?: ld.LDContext): boolean => {
  const keyA = ldContextIdentityKey(a);
  const keyB = ldContextIdentityKey(b);
  if (keyA !== null || keyB !== null) {
    return keyA === keyB;
  }
  return anonymousKeyOf(a) === anonymousKeyOf(b);
};
const anonymousKeyOf = (context?: ld.LDContext): string | undefined => {
  if (!context || typeof context !== "object") {
    return undefined;
  }
  const single = context as {
    kind?: unknown;
    key?: unknown;
  };
  if (typeof single.key === "string") {
    return single.key;
  }
  const user = (context as {
    user?: {
      key?: unknown;
    };
  }).user;
  return typeof user?.key === "string" ? user.key : undefined;
};

/**
 * Records the LaunchDarkly exposure for a bootstrapped flag, once per mount.
 * Evaluating is what enrols, so this is the only place a wrapped page touches
 * the live SDK — and the identity guard is what stops it enrolling a context
 * other than the one whose value is on screen.
 */
export const useRecordExposure = (flag: Flag) => {
  const client = useLDClient();
  const bootstrap = useFlagBootstrap();
  // `LDProvider` applies its `context` prop only at initialization, and it is
  // not remounted across client-side navigation — so on a navigation that
  // changes identity the client still holds the previous one when this first
  // runs, and nothing about `client` or `bootstrap` changes when the auth
  // provider's `identify` later lands. Without this the retry never happens
  // and the exposure is lost for that page view.
  const identifyGeneration = useFlagIdentifyGeneration();
  const recorded = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: identifyGeneration is intentionally unused in the body — it exists solely to re-run this after an identify lands, since `client` is referentially stable and mutates in place
  useEffect(() => {
    if (recorded.current || !client || bootstrap?.status !== "ready") {
      return;
    }
    let cancelled = false;
    // The SDK silently drops the evaluation event from a read made during its
    // context-validation window (values are correct, the exposure never
    // ships), so the enrolling read must wait for ready. The guards re-check
    // after the wait because both can change while it resolves.
    void client.waitUntilReady().then(() => {
      if (cancelled || recorded.current) {
        return;
      }
      if (!sameIdentity(client.getContext(), bootstrap.context)) {
        return;
      }
      // `allFlags` reads without emitting an evaluation event; `variation` is
      // what enrols. If the SDK's own fetch already replaced the bootstrapped
      // value, enrolling here would attribute the exposure to an arm this
      // visitor never saw.
      if (client.allFlags()[flag as string] !== bootstrap.flags[flag]) {
        return;
      }
      recorded.current = true;
      client.variation(flag as string, undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [client, bootstrap, flag, identifyGeneration]);
};
export type { FlagBootstrap };